import * as React from 'react';
import * as styled from './styled';
import {useFormikContext} from 'formik';
import {TaskObject, TestAnswerBlock} from 'api/task/types';
import Checkbox from 'components/Checkbox';
import Radio from 'components/Radio';
import ControlButton, {ControlButtonScale} from 'components/ControlButton';
import {useBlockContext} from '../../../../../../hooks/useBlockContext';
import {IconChat, IconTrash} from 'icons';
import {AnswerProps} from './types';

/**
 * The answer block of a test template.
 * @param {AnswerProps} props
 * @return {React.ReactElement<AnswerProps>}
 * @constructor
 */
export default function Answer(
    props: AnswerProps,
): React.ReactElement<AnswerProps> {
  const {setFieldValue} = useFormikContext<Partial<TaskObject>>();
  const block = useBlockContext();

  // @ts-ignore
  const answers = block?.answers || [] as TestAnswerBlock[];

  // @ts-ignore
  const isSeveral = block?.isSeveral as boolean;

  /**
   * Render input.
   * @return {JSX.Element}
   */
  function renderInput(): JSX.Element {
    /**
     * The change event handler.
     * @param {React.ChangeEvent<HTMLInputElement>} event
     * @return {void}
     */
    function handleChange(
      event: React.ChangeEvent<HTMLInputElement>,
    ): void {
      if (!isSeveral) {
        setFieldValue(
          `content.blocks[${block.index}].answers`,
          answers.map((answer: TestAnswerBlock) => ({
            ...answer,
            isTrue: false,
          })),
        );
      }

      setFieldValue(
        `content.blocks[${block.index}].answers[${props.index}].isTrue`,
        event.target.checked,
      );
    }

    /**
     * Get input component.
     * @return {(props: CheckboxProps) => React.ReactElement}
     */
    function getInputComponent() {
      return isSeveral ? Checkbox : Radio;
    }

    const InputComponent = getInputComponent();

    return (
      <styled.InputContainer>
        <InputComponent
          name={`block-${block.index}`}
          checked={props.isTrue}
          onChange={handleChange}
        />
      </styled.InputContainer>
    );
  }

  /**
   * Render text.
   * @return {JSX.Element}
   */
  function renderText(): JSX.Element {
    /**
     * The change event handler.
     * @param {React.ChangeEvent<HTMLTextAreaElement>} event
     * @return {void}
     */
    function handleChange(
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ): void {
      setFieldValue(
        `content.blocks[${block.index}].answers[${props.index}].text`,
        event.target.value,
      );
    }

    return (
      <styled.Text
        value={props.text}
        placeholder="Введите ответ"
        onChange={handleChange}
        required
      />
    );
  }

  /**
   * Render controls
   * @return {JSX.Element}
   */
  function renderControls(): JSX.Element {
    /**
     * Render note.
     * @return {JSX.Element}
     */
    function renderNote(): JSX.Element {
      /**
       * Is the control active.
       * @return {boolean}
       */
      function isActive(): boolean {
        return Boolean(props.note !== undefined);
      }

      /**
       * The click event handler.
       * @return {void}
       */
      function handleClick(): void {
        setFieldValue(
          `content.blocks[${block.index}].answers[${props.index}].note`,
          isActive() ? undefined : ''
        );
      }

      return (
        <styled.Control isActive={isActive()}>
          <ControlButton
            type="button"
            scale={ControlButtonScale.Small}
            onClick={handleClick}
          >
            <IconChat />
          </ControlButton>
        </styled.Control>
      );
    }

    /**
     * Render delete.
     * @return {JSX.Element}
     */
    function renderDelete(): JSX.Element {
      /**
       * The click event handler.
       */
      function handleClick(): void {
        setFieldValue(
          `content.blocks[${block.index}].answers`,
          answers.filter((_: never, _index: number) => (
            _index !== props.index
          )),
        );
      }

      return (
        <styled.Control>
          <ControlButton
            type="button"
            scale={ControlButtonScale.Small}
            onClick={handleClick}
          >
            <IconTrash />
          </ControlButton>
        </styled.Control>
      );
    }

    return (
      <styled.Controls>
        {renderNote()}
        {renderDelete()}
      </styled.Controls>
    );
  }

  /**
   * Render note.
   * @return {JSX.Element}
   */
  function renderNote(): JSX.Element | undefined {
    /**
     * The change event handler.
     * @param {React.ChangeEvent<HTMLTextAreaElement>} event
     * @return {void}
     */
    function handleChange(
      event: React.ChangeEvent<HTMLTextAreaElement>,
    ): void {
      setFieldValue(
        `content.blocks[${block.index}].answers[${props.index}].note`,
        event.target.value,
      );
    }

    return props.note !== undefined ? (
      <styled.Note
        value={props.note}
        placeholder="Введите комментарий к ответу. Ученик увидит его после того, как выберет этот ответ."
        onChange={handleChange}
        required
      />
    ) : undefined;
  }

  return (
    <styled.Wrapper>
      {renderInput()}
      {renderText()}
      {renderControls()}
      {renderNote()}
    </styled.Wrapper>
  );
}
